import { z } from 'zod';

import {
  ClosedReason,
  EntityId,
  EpochMs,
  I18nText,
  SessionRole,
  ToneName,
  Uid,
  Ulid,
  Via,
} from './primitives';

/**
 * Firebase Realtime Database nodes (docs/02 §2). Times are epoch ms here (not
 * ISO), because RTDB rules compare them with `now`.
 */

export const PairingStatus = z.enum(['pending', 'approved', 'denied', 'expired']);
export type PairingStatus = z.infer<typeof PairingStatus>;

/** `/pairing/{deviceCode}` — readable by anyone holding the device code. */
export const PairingNode = z.object({
  status: PairingStatus,
  sessionId: Ulid.optional(),
  updatedAt: EpochMs,
});
export type PairingNode = z.infer<typeof PairingNode>;

/** `/sessions/{sid}/meta` */
export const SessionMeta = z.object({
  joinCode: z.string().length(6),
  hostUid: Uid,
  hostName: z.string(),
  status: z.enum(['active', 'closed']),
  /**
   * Why it closed, written together with `status='closed'` (QA 2026-09-15 UX-566). Optional: a
   * session that is still `active` has none, and RTDB stores no nulls.
   */
  closedReason: ClosedReason.optional(),
  startedAt: EpochMs,
  expiresAt: EpochMs,
  /** Host feature snapshot, for UI gating on phones. */
  features: z.array(z.string()),
  /** Bumped on every API write to the session. */
  version: z.number().int().nonnegative(),
});
export type SessionMeta = z.infer<typeof SessionMeta>;

/** `/sessions/{sid}/members/{uid}/presence` — client-write by that uid. */
export const Presence = z.object({
  online: z.boolean(),
  lastSeen: EpochMs,
});
export type Presence = z.infer<typeof Presence>;

/** `/sessions/{sid}/members/{uid}` */
export const Member = z.object({
  name: z.string(),
  photo: z.string().optional(),
  role: SessionRole,
  joinedAt: EpochMs,
  presence: Presence.optional(),
});
export type Member = z.infer<typeof Member>;

/** Whole `members/` node keyed by uid. */
export const MembersNode = z.record(Uid, Member);
export type MembersNode = z.infer<typeof MembersNode>;

export const QueueItemStatusNode = z.enum(['queued', 'playing', 'played']);

/** `/sessions/{sid}/queue/{itemId}` */
export const QueueItem = z.object({
  songId: EntityId,
  title: z.string(),
  artistNames: z.string(),
  /**
   * Signed; refreshed by the API when re-emitted. Optional as well as nullable because RTDB
   * stores no nulls: a `null` written by the API simply leaves the key out of the snapshot.
   */
  imageUrl: z.string().nullable().optional(),
  tone: ToneName,
  toneLabel: I18nText,
  addedBy: z.object({ uid: Uid, name: z.string(), photo: z.string().optional() }),
  addedVia: Via,
  /** Integer, gaps of 1000 for cheap reorders. */
  order: z.number().int(),
  status: QueueItemStatusNode,
  addedAt: EpochMs,
  startedAt: EpochMs.optional(),
  playedAt: EpochMs.optional(),
});
export type QueueItem = z.infer<typeof QueueItem>;

/** Whole `queue/` node keyed by item id. */
export const QueueNode = z.record(Ulid, QueueItem);
export type QueueNode = z.infer<typeof QueueNode>;

export const PlayerCommandType = z.enum(['play', 'pause', 'playItem', 'next', 'stop']);
export type PlayerCommandType = z.infer<typeof PlayerCommandType>;

/** `/sessions/{sid}/player/command` */
export const PlayerCommandNode = z.object({
  id: Ulid,
  type: PlayerCommandType,
  itemId: Ulid.optional(),
  issuedBy: Uid,
  issuedVia: Via,
  at: EpochMs,
});
export type PlayerCommandNode = z.infer<typeof PlayerCommandNode>;

export const PlayerState = z.enum(['idle', 'playing', 'paused']);
export type PlayerState = z.infer<typeof PlayerState>;

/** `/sessions/{sid}/player` */
export const PlayerNode = z.object({
  state: PlayerState,
  currentItemId: Ulid.optional(),
  command: PlayerCommandNode.optional(),
});
export type PlayerNode = z.infer<typeof PlayerNode>;

export const TvPlaybackState = z.enum(['idle', 'loading', 'playing', 'paused', 'error']);
export type TvPlaybackState = z.infer<typeof TvPlaybackState>;

/** `/sessions/{sid}/tvState` — client-write by the TV token of this session. */
export const TvState = z.object({
  state: TvPlaybackState,
  currentItemId: Ulid.optional(),
  positionMs: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  tone: ToneName,
  updatedAt: EpochMs,
  appVersion: z.string(),
});
export type TvState = z.infer<typeof TvState>;

/**
 * `/sessions/{sid}/notices/{id}` — written by the API when a guest of a web party asks for a song
 * the host's plan does not cover (docs/13 §2, E-22). Members read it; nobody else writes it.
 */
export const SessionNotice = z.object({
  type: z.literal('premium_blocked'),
  name: z.string(),
  title: z.string(),
  at: EpochMs,
});
export type SessionNotice = z.infer<typeof SessionNotice>;

/** Whole `notices/` node keyed by notice id. */
export const NoticesNode = z.record(Ulid, SessionNotice);
export type NoticesNode = z.infer<typeof NoticesNode>;

/** `/sessions/{sid}` */
export const SessionNode = z.object({
  meta: SessionMeta,
  members: MembersNode.optional(),
  queue: QueueNode.optional(),
  player: PlayerNode.optional(),
  tvState: TvState.optional(),
  notices: NoticesNode.optional(),
});
export type SessionNode = z.infer<typeof SessionNode>;

/** Typed RTDB paths, shared by the client listeners and the server writer. */
export const rtdbPaths = {
  pairing: (deviceCode: string) => `/pairing/${deviceCode}`,
  session: (sid: string) => `/sessions/${sid}`,
  meta: (sid: string) => `/sessions/${sid}/meta`,
  members: (sid: string) => `/sessions/${sid}/members`,
  member: (sid: string, uid: string) => `/sessions/${sid}/members/${uid}`,
  presence: (sid: string, uid: string) => `/sessions/${sid}/members/${uid}/presence`,
  queue: (sid: string) => `/sessions/${sid}/queue`,
  queueItem: (sid: string, itemId: string) => `/sessions/${sid}/queue/${itemId}`,
  player: (sid: string) => `/sessions/${sid}/player`,
  playerCommand: (sid: string) => `/sessions/${sid}/player/command`,
  tvState: (sid: string) => `/sessions/${sid}/tvState`,
  notices: (sid: string) => `/sessions/${sid}/notices`,
} as const;

/**
 * RTDB drops empty arrays and `SessionMeta.features` is required, so a host with no plan would leave
 * a `meta` node every client discards. It is stored as this placeholder, which is no feature at all;
 * REST `session.features` stays `[]` (docs/13 §2).
 */
export const RTDB_EMPTY_FEATURES: readonly string[] = ['web.free'];

export const rtdbFeatures = (features: readonly string[]): string[] => [
  ...(features.length > 0 ? features : RTDB_EMPTY_FEATURES),
];

/** Gap between consecutive `order` values (docs/02 §2). */
export const QUEUE_ORDER_GAP = 1000;
